import useCustomHistory from '@onepercentio/one-ui/hooks/useCustomHistory'
import { useMarketplaceUser } from 'context/MarketplaceUser'
import { Sale } from 'core/logic/asset/asset.types'
import {
  TO_COLLECTION_ITEM_DETAIL,
  TO_MARKETPLACE_OFFER_DETAILS,
} from 'core/modules/router'
import { ComponentProps, useEffect, useMemo, useState } from 'react'
import MultiStepConfirmation, {
  AutoConfirmation,
  AutoConfirmationStatus,
  CONFIRMATION_STATES,
} from './Confirm.view'
import { useMarketplace } from 'context/Marketplace'
import { OfferConfirmationType, useDisplayModel } from 'context/DisplayModels'
import { useUser } from 'context/User'
import useLocalStorage from '@onepercentio/one-ui/hooks/persistence/useLocalStorage'
import { draftId } from '../OfferType/DigitalTwin/DigitalTwin.logic'
import { useLockRouteChange } from 'components/CustomRouter/CustomRouter'
import { t } from 'translate/i18n'
import { useProgressUI } from 'openspace/service/Progress'
import Spacing from '@onepercentio/one-ui/components/Spacing'

export default function ConfirmLogic({
  offer,
  asset,
  onGoBack,
}: Pick<
  ComponentProps<typeof MultiStepConfirmation>,
  'offer' | 'asset' | 'onGoBack'
>) {
  const { profile } = useUser()
  const [_, __, deleteDraft] = useLocalStorage(
    draftId({ id: offer.asset.id, tokenId: offer.tokenId! }),
    null
  )
  const [isAllowedForTransfer, setISAllowed] = useState<boolean>()
  const [finalOfferId, setOfferId] = useState<Sale['offerId']>()
  const {
    getNftMovementAllowance,
    control,
    approveNFTManagement,
    createOfferOnly: _createOfferOnly,
  } = useMarketplaceUser()
  const canExit = finalOfferId
  useLockRouteChange(
    !canExit,
    t('offer.creation.steps.creating.routeChangeWarning')
  )
  const creationProgress = useProgressUI('create_offer')
  const { sales } = useMarketplace()
  const { updateLastAddress, updateProfile } = useUser()
  const confirmationModel = useDisplayModel('offerConfirmation.mode')

  const history = useCustomHistory()

  useEffect(() => {
    getNftMovementAllowance(offer.tokenId).then(setISAllowed)
  }, [getNftMovementAllowance])

  function _approveNFTManagement() {
    return approveNFTManagement(offer.tokenId).then(() => setISAllowed(true))
  }

  async function _createOffer() {
    if (offer.paymentData && !profile!.personalData?.pixKey)
      await updateProfile({ personalData: { pixKey: offer.paymentData } })
    if (offer.address) await updateLastAddress(offer.address!)
    return _createOfferOnly(offer)
      .then(setOfferId)
      .then(() => deleteDraft())
  }

  function _goToOffer() {
    history.go(-1)
    history.replace(
      TO_MARKETPLACE_OFFER_DETAILS(asset.id, finalOfferId!, asset.tokenId)
    )
  }

  function _retry() {
    if (isAllowedForTransfer === undefined)
      getNftMovementAllowance()
        .then(setISAllowed)
        .catch(() => {})
    if (isAllowedForTransfer === false) _approveNFTManagement()
    if (isAllowedForTransfer === true) _createOffer()
  }

  switch (confirmationModel) {
    case OfferConfirmationType.AUTO:
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const status = useMemo(() => {
        if (isAllowedForTransfer === undefined)
          return AutoConfirmationStatus.LOADING
        if (isAllowedForTransfer === false)
          return AutoConfirmationStatus.NEED_ALLOWANCE
        if (finalOfferId !== undefined) return AutoConfirmationStatus.CONFIRMED
        return AutoConfirmationStatus.NEED_CONFIRMATION
      }, [isAllowedForTransfer, finalOfferId])
      return (
        <>
          <AutoConfirmation
            label={{
              confirmed: {
                description: 'offer.creation.steps.created.description',
                title: 'offer.creation.steps.created.title',
              },
              loading: 'offer.creation.steps.creating.title',
            }}
            onApproveAllowance={_approveNFTManagement}
            onConfirmOperation={_createOffer}
            onGoToItem={_goToOffer}
            status={status}
            progressLabel={
              status !== AutoConfirmationStatus.CONFIRMED ? (
                <>
                  <Spacing size='16' />
                  {creationProgress}
                </>
              ) : null
            }
            onCancel={onGoBack}
          />
        </>
      )
    default:
      return (
        <MultiStepConfirmation
          offer={offer}
          asset={asset}
          loading={control.loading}
          error={!!control.error}
          state={
            !isAllowedForTransfer
              ? CONFIRMATION_STATES.CHECKING_APPROVAL
              : CONFIRMATION_STATES.READY_FOR_CONFIRMING
          }
          onApprove={_approveNFTManagement}
          onCreate={_createOffer}
          offerId={finalOfferId}
          onGoToOffer={_goToOffer}
          onRetry={_retry}
          onGoBack={() =>
            history.goBackWithFallback(
              TO_COLLECTION_ITEM_DETAIL(asset.id, asset.tokenId)
            )
          }
        />
      )
  }
}
